import React, { createContext, useContext, useState, ReactNode, useEffect, useRef } from 'react';
import { useLocation } from 'react-router-dom';
import { setSelectedOrganizationId } from '../redux/slices/organizationsSlice';
import { useAppDispatch, useAppSelector } from '../hooks/reduxHooks';
import { RootState } from '../redux/store';

interface OrganizationFilterContextType {
    selectedOrganization: any;
    setSelectedOrganization: (organization: any) => void;
    clearSelectedOrganization: () => void;
}

const OrganizationFilterContext = createContext<OrganizationFilterContextType | undefined>(undefined);

interface OrganizationFilterProviderProps {
    children: ReactNode;
}

export const OrganizationFilterProvider: React.FC<OrganizationFilterProviderProps> = ({ children }) => {
    const [selectedOrganization, setSelectedOrganization] = useState<any>(null);
    const { selectedOrganizationId } = useAppSelector((state: RootState) => state.organizations);   

    const location = useLocation();
    const dispatch = useAppDispatch();
    
    const getModuleFromPath = (pathname: string) => {
        const parts = pathname.split('/').filter(Boolean);
        return parts[1] || '';
    };

    const prevModuleRef = useRef<string>(getModuleFromPath(location.pathname));

    useEffect(() => {
        const currentModule = getModuleFromPath(location.pathname);
        const prevModule = prevModuleRef.current;

        if (prevModule !== null && prevModule !== currentModule) {
            setSelectedOrganization(null);
            dispatch(setSelectedOrganizationId(null));
        }

        prevModuleRef.current = currentModule;
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [location.pathname]);

    useEffect(() => {
        setSelectedOrganization(selectedOrganizationId);
    }, [selectedOrganizationId])

    const clearSelectedOrganization = () => {
        setSelectedOrganization(null);
    };

    const value: OrganizationFilterContextType = {
        selectedOrganization,
        setSelectedOrganization,
        clearSelectedOrganization
    };

    return (
        <OrganizationFilterContext.Provider value={value}>
            {children}
        </OrganizationFilterContext.Provider>
    );
};

export const useOrganizationFilter = (): OrganizationFilterContextType => {
    const context = useContext(OrganizationFilterContext);
    if (context === undefined) {
        throw new Error('useOrganizationFilter must be used within an OrganizationFilterProvider');
    }
    return context;
}; 